import { HttpClient } from "@utils/httpClient";
import type {
  DashboardData,
  Granularity,
  MonitoringBreakdown,
  MonitoringDimension,
  ModelUsage,
  MonitoringFacets,
  MonitoringFilters,
  TransactionDetail,
  TransactionPage,
  TransactionSort,
} from "@models/adminMonitoring";

const BASE_URL = import.meta.env.VITE_XPLATFORM_CORE_API_BASE_URL;

/** Query parameters of the shared filter contract (unset filters are omitted). */
export const filterParams = (filters: MonitoringFilters): URLSearchParams => {
  const params = new URLSearchParams({ from: filters.from, to: filters.to });
  const optional: Array<[string, string | undefined]> = [
    ["organisation_id", filters.organisationId],
    ["module", filters.module],
    ["service", filters.service],
    ["provider", filters.provider],
    ["model", filters.model],
    ["capability", filters.capability],
    ["user_id", filters.userId],
    ["currency", filters.currency],
    ["ref_type", filters.refType],
    ["ref_id", filters.refId],
    ["step", filters.step],
    ["root_ref_type", filters.rootRefType],
    ["root_ref_id", filters.rootRefId],
  ];
  for (const [key, value] of optional) {
    if (value) params.append(key, value);
  }
  return params;
};

const withExtra = (
  filters: MonitoringFilters,
  extra: Record<string, string | number | boolean | undefined | null>,
): string => {
  const params = filterParams(filters);
  for (const [key, value] of Object.entries(extra)) {
    if (value !== undefined && value !== null && value !== "") params.append(key, String(value));
  }
  return params.toString();
};

/**
 * Core's admin monitoring / dashboard BFF. The organisation scope is decided
 * server-side; a cross-organisation caller may pass `organisationId` (or omit
 * it for every organisation), anyone else is pinned to their own.
 */
class AdminMonitoringApi {
  private readonly http: HttpClient;

  constructor() {
    if (!BASE_URL) {
      throw new Error("VITE_XPLATFORM_CORE_API_BASE_URL is not configured");
    }
    this.http = new HttpClient(BASE_URL);
  }

  private get<T>(path: string, signal?: AbortSignal): Promise<T> {
    return this.http.makeAuthenticatedRequest<T>(path, { method: "GET", signal });
  }

  /**
   * Everything the dashboard renders. `recent: false` leaves out the latest
   * transactions, which read the raw events of the window: load them with
   * {@link recentTransactions} so the rest of the page never waits for them.
   */
  dashboard(
    filters: MonitoringFilters,
    options: { granularity?: Granularity; splitBy?: MonitoringDimension; recent?: boolean } = {},
    signal?: AbortSignal,
  ): Promise<DashboardData> {
    const query = withExtra(filters, {
      granularity: options.granularity,
      split_by: options.splitBy,
      recent: options.recent,
    });
    return this.get(`/admin/dashboard?${query}`, signal);
  }

  /** The dashboard's latest transactions (dashboard permission, not monitoring). */
  recentTransactions(filters: MonitoringFilters, limit = 10, signal?: AbortSignal): Promise<TransactionPage> {
    return this.get(`/admin/dashboard/recent-transactions?${withExtra(filters, { limit })}`, signal);
  }

  breakdown(
    filters: MonitoringFilters,
    dimension: MonitoringDimension,
    signal?: AbortSignal,
  ): Promise<MonitoringBreakdown> {
    return this.get(`/admin/monitoring/breakdown/${dimension}?${filterParams(filters)}`, signal);
  }

  /** Which module and service called which model in the window. */
  modelUsage(filters: MonitoringFilters, signal?: AbortSignal): Promise<ModelUsage> {
    return this.get(`/admin/monitoring/model-usage?${filterParams(filters)}`, signal);
  }

  facets(filters: MonitoringFilters, signal?: AbortSignal): Promise<MonitoringFacets> {
    return this.get(`/admin/monitoring/facets?${filterParams(filters)}`, signal);
  }

  transactions(
    filters: MonitoringFilters,
    page: { sort: TransactionSort; limit: number; offset: number },
    signal?: AbortSignal,
  ): Promise<TransactionPage> {
    const query = withExtra(filters, {
      sort: page.sort,
      limit: page.limit,
      offset: page.offset,
    });
    return this.get(`/admin/monitoring/transactions?${query}`, signal);
  }

  /** One transaction; without a window it is read over its whole lifetime. */
  transaction(
    groupId: string,
    window?: { from: string; to: string },
    signal?: AbortSignal,
  ): Promise<TransactionDetail> {
    const query = window ? `?${new URLSearchParams(window)}` : "";
    return this.get(`/admin/monitoring/transactions/${encodeURIComponent(groupId)}${query}`, signal);
  }
}

let instance: AdminMonitoringApi | null = null;

/** Lazily built so a missing base URL fails on first use, not at module load. */
export const adminMonitoringApi = (): AdminMonitoringApi => {
  instance ??= new AdminMonitoringApi();
  return instance;
};
